import { posthog } from "@/lib/posthog"
import { NavigateFunction } from "react-router"
import { toast } from "sonner"
import { HTTPError } from "ky"
import { handleNetworkError } from "./base"

/**
 * Result type for batch operations
 */
export interface BatchOperationResult<T> {
  successful: T[]
  failed: Array<{ item: T; error: string }>
  totalAttempted: number
  successCount: number
  failureCount: number
}

/**
 * Error handling options
 */
export interface ErrorHandlingOptions {
  /** Description of the operation that failed (e.g., "create product sheet") */
  operation: string
  /** Additional context for debugging (e.g., { truckNumber, productId }) */
  context?: Record<string, any>
  /** Whether to show a toast notification (default: true) */
  showToast?: boolean
  /** Whether to suppress all user notifications (default: false) */
  silent?: boolean
  /** Custom error message to show to user */
  customMessage?: string
}

/**
 * Result of error handling
 */
export interface ErrorHandlingResult {
  /** Whether the error was handled by the network error handler (redirect occurred) */
  handled: boolean
  /** User-friendly error message */
  userMessage: string
  /** Original error object */
  originalError: any
}

/**
 * Unified error handler for API operations
 *
 * This function:
 * - Logs errors to PostHog with context
 * - Shows toast notifications to users
 * - Handles network errors (redirects to /network-error)
 * - Extracts user-friendly messages from HTTPError responses
 *
 * @param error - The error object
 * @param options - Error handling options
 * @param navigate - React Router navigate function
 * @returns Error handling result
 *
 * @example
 * ```typescript
 * try {
 *   await createProduct(data)
 * } catch (err) {
 *   const result = await handleApiError(err, {
 *     operation: "create product",
 *     context: { productId: data.id },
 *   }, navigate)
 * }
 * ```
 */
export async function handleApiError(
  error: any,
  options: ErrorHandlingOptions,
  navigate: NavigateFunction
): Promise<ErrorHandlingResult> {
  const {
    operation,
    context = {},
    showToast = true,
    silent = false,
    customMessage,
  } = options

  // Log to PostHog with full context
  posthog.capture("api_error", {
    operation,
    error_name: error?.name,
    error_message: error?.message,
    ...context,
  })

  // Try network error handler first (handles TypeError, AbortError)
  const networkHandled = await handleNetworkError(error, navigate)

  // Extract user-friendly message
  let userMessage = customMessage || `Failed to ${operation}`

  if (error instanceof HTTPError) {
    // HTTPError already has user_message extracted by beforeError hook
    userMessage = error.message || userMessage
  } else if (error?.message) {
    // Use error message if available
    userMessage = error.message
  }

  // Show toast notification if not handled by network error handler and not silent
  if (!networkHandled && !silent && showToast) {
    toast.error(userMessage)
  }

  return {
    handled: networkHandled,
    userMessage,
    originalError: error,
  }
}

/**
 * Creates a human-readable summary of a batch operation
 *
 * @param result - Batch operation result
 * @param itemName - Name of items being operated on (e.g., "product", "truck")
 * @returns Summary string
 *
 * @example
 * createBatchSummary(result, "product")
 * // => "Created 3 of 5 products. 2 failed."
 */
export function createBatchSummary<T>(
  result: BatchOperationResult<T>,
  itemName: string
): string {
  const { successCount, failureCount, totalAttempted } = result
  const pluralizedItem = totalAttempted === 1 ? itemName : `${itemName}s`

  if (failureCount === 0) {
    // All succeeded
    return `Successfully created ${successCount} ${pluralizedItem}`
  } else if (successCount === 0) {
    // All failed
    return `Failed to create all ${totalAttempted} ${pluralizedItem}`
  } else {
    // Partial success
    return `Created ${successCount} of ${totalAttempted} ${pluralizedItem}. ${failureCount} failed.`
  }
}

/**
 * Shows appropriate toast based on batch operation result
 *
 * - All succeeded: Success toast (auto-hide)
 * - Partial success: Warning toast (longer duration)
 * - All failed: Error toast (persistent)
 *
 * @param result - Batch operation result
 * @param itemName - Name of items being operated on
 * @param options - Additional toast options
 *
 * @example
 * showBatchResultToast(batchResult, "product")
 */
export function showBatchResultToast<T>(
  result: BatchOperationResult<T>,
  itemName: string,
  options?: {
    /** Additional description to show below main message */
    description?: string
    /** Toast duration in milliseconds */
    duration?: number
  }
): void {
  const summary = createBatchSummary(result, itemName)
  const { successCount, failureCount } = result

  if (failureCount === 0) {
    // All succeeded - success toast
    toast.success(summary, {
      description: options?.description,
      duration: options?.duration || 4000,
    })
  } else if (successCount === 0) {
    // All failed - error toast
    toast.error(summary, {
      description: options?.description || "Please try again or contact support if the issue persists.",
      duration: options?.duration || 6000,
    })
  } else {
    // Partial success - warning toast
    toast.warning(summary, {
      description: options?.description || "Some operations completed successfully.",
      duration: options?.duration || 6000,
    })
  }
}

/**
 * Creates an empty batch result to track operations
 *
 * @param totalAttempted - Total number of operations to attempt
 * @returns Empty batch result
 *
 * @example
 * const batchResult = createBatchResult<string>(products.length)
 */
export function createBatchResult<T>(totalAttempted: number): BatchOperationResult<T> {
  return {
    successful: [],
    failed: [],
    totalAttempted,
    successCount: 0,
    failureCount: 0,
  }
}
